<template>
  <div class="course-detail">
    <!-- Hero Banner -->
    <div class="course-hero px-6 py-5 mb-5 rounded-lg">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-2 mb-3">
          <v-btn icon dark small @click="$emit('back')" class="mr-1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="text-2xl font-bold text-white mb-1">{{ course.title }}</h1>
            <div class="flex items-center gap-3 flex-wrap">
              <v-chip small dark color="rgba(255,255,255,0.2)" class="font-semibold">
                <v-icon left x-small>mdi-tag</v-icon>{{ course.category }}
              </v-chip>
              <v-chip small dark :color="difficultyColor" class="font-semibold">
                <v-icon left x-small>mdi-signal</v-icon>{{ course.difficulty }}
              </v-chip>
              <span class="text-white text-sm opacity-80">
                <v-icon x-small dark class="mr-1">mdi-account-tie</v-icon>{{ course.instructor }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <v-btn small outlined dark class="normal-case" @click="editCourse">
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
          <v-btn
            small
            outlined
            dark
            class="normal-case"
            color="red lighten-2"
            @click="confirmDelete"
          >
            <v-icon left small>mdi-delete</v-icon>Delete
          </v-btn>
        </div>
      </div>

      <p class="text-white text-sm opacity-90 mt-2 mb-3 max-w-3xl">{{ course.description }}</p>

      <!-- Stats Row -->
      <div class="flex gap-5 flex-wrap">
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-book-multiple</v-icon>
          <span>{{ course.lessons.length }} Lessons</span>
        </div>
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-clock-outline</v-icon>
          <span>{{ totalDuration }}</span>
        </div>
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-account-group</v-icon>
          <span>{{ course.enrolledUsers.length }} Enrolled</span>
        </div>
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-check-circle</v-icon>
          <span>{{ myProgress }}% Complete</span>
        </div>
      </div>

      <!-- Progress Bar -->
      <v-progress-linear
        :value="myProgress"
        color="white"
        height="6"
        rounded
        class="mt-3"
        background-color="rgba(255,255,255,0.25)"
      />
    </div>

    <div class="flex gap-5">
      <!-- Left: Lesson List -->
      <div class="flex-1">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold" style="color: #21427d">
            <v-icon class="mr-1" color="#21427d">mdi-format-list-bulleted</v-icon>
            Course Content
          </h2>
          <v-btn
            color="#21427d"
            small
            class="white--text normal-case rounded-lg"
            depressed
            @click="showLessonForm = true"
          >
            <v-icon left small>mdi-plus</v-icon>Add Lesson
          </v-btn>
        </div>

        <!-- Accordion Lesson List -->
        <v-expansion-panels v-model="openPanels" multiple flat class="lesson-accordion">
          <v-expansion-panel
            v-for="(lesson, index) in sortedLessons"
            :key="lesson._id"
            class="lesson-panel mb-2 rounded-lg"
          >
            <v-expansion-panel-header class="lesson-panel-header py-2 px-4">
              <div class="flex items-center gap-3 w-full">
                <!-- Completion Checkbox -->
                <v-checkbox
                  :input-value="isLessonDone(lesson._id)"
                  @change="toggleLessonCompletion(lesson._id, $event)"
                  hide-details
                  class="ma-0 pa-0 shrink-0"
                  color="#4caf50"
                  @click.stop
                />

                <!-- Lesson Number Badge -->
                <div
                  class="lesson-number flex-shrink-0 text-xs font-bold"
                  :style="{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isLessonDone(lesson._id) ? 'white' : '#21427d',
                    background: isLessonDone(lesson._id) ? '#4caf50' : '#e8eef8'
                  }"
                >
                  {{ index + 1 }}
                </div>

                <!-- Lesson Title -->
                <div class="flex-1 min-w-0">
                  <div
                    class="font-semibold text-sm"
                    :class="{ 'line-through text-gray-400': isLessonDone(lesson._id) }"
                  >
                    {{ lesson.title }}
                  </div>
                </div>

                <!-- Duration -->
                <div class="flex items-center gap-3 flex-shrink-0">
                  <v-chip x-small outlined color="#21427d" class="font-medium">
                    <v-icon x-small left>mdi-clock-outline</v-icon>{{ lesson.duration }}
                  </v-chip>
                  <!-- Video status chip -->
                  <v-chip
                    v-if="lesson.hlsStatus"
                    x-small
                    :color="lesson.hlsStatus === 'READY' ? 'success lighten-4' : 'orange lighten-4'"
                    :text-color="
                      lesson.hlsStatus === 'READY' ? 'success darken-2' : 'orange darken-3'
                    "
                    class="font-medium"
                  >
                    <v-icon x-small left>{{
                      lesson.hlsStatus === 'READY' ? 'mdi-video-check' : 'mdi-clock-outline'
                    }}</v-icon>
                    {{ lesson.hlsStatus === 'READY' ? 'HLS Ready' : 'Processing' }}
                  </v-chip>

                  <v-btn icon x-small color="#21427d" @click.stop="playLesson(lesson)">
                    <v-icon small>mdi-play-circle</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="px-4 pb-3">
              <div class="pl-14">
                <p class="text-sm text-gray-600 mb-3">{{ lesson.description }}</p>
                <div class="flex gap-2">
                  <v-btn
                    x-small
                    outlined
                    color="#21427d"
                    class="normal-case"
                    @click="playLesson(lesson)"
                  >
                    <v-icon left x-small>mdi-play</v-icon>Play Lesson
                  </v-btn>
                  <v-btn
                    x-small
                    outlined
                    color="orange darken-2"
                    class="normal-case"
                    @click="openVideoUpload(lesson)"
                  >
                    <v-icon left x-small>mdi-upload</v-icon>Upload Video
                  </v-btn>
                  <v-btn x-small outlined class="normal-case" @click="editLesson(lesson)">
                    <v-icon left x-small>mdi-pencil</v-icon>Edit
                  </v-btn>
                  <v-btn
                    x-small
                    outlined
                    color="red"
                    class="normal-case"
                    @click="deleteLessonConfirm(lesson)"
                  >
                    <v-icon left x-small>mdi-delete</v-icon>Remove
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="course.lessons.length === 0" class="text-center py-10">
          <v-icon size="48" color="#bdbdbd" class="mb-3">mdi-book-open-variant</v-icon>
          <p class="text-gray-500">No lessons yet. Add your first lesson to get started!</p>
        </div>
      </div>

      <!-- Right Sidebar - Enrolled Users -->
      <div class="course-sidebar" style="width: 280px">
        <v-card class="rounded-lg" outlined>
          <v-card-title class="text-sm font-bold pb-2" style="color: #21427d">
            <v-icon small class="mr-2" color="#21427d">mdi-account-group</v-icon>
            Enrolled Users
          </v-card-title>
          <v-divider />
          <v-list dense class="pa-0">
            <v-list-item v-for="uid in course.enrolledUsers" :key="uid" class="py-1">
              <v-list-item-avatar size="30">
                <v-avatar size="30" color="#21427d">
                  <span class="white--text text-xs font-bold">{{ getUserInitials(uid) }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-sm">{{ getUserName(uid) }}</v-list-item-title>
                <v-list-item-subtitle class="text-xs"
                  >{{ getUserProgress(uid) }}% complete</v-list-item-subtitle
                >
              </v-list-item-content>
              <v-list-item-action>
                <v-progress-circular
                  :value="getUserProgress(uid)"
                  :size="28"
                  :width="3"
                  :color="getProgressColor(getUserProgress(uid))"
                >
                  <span style="font-size: 8px; font-weight: 700">{{ getUserProgress(uid) }}</span>
                </v-progress-circular>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-if="!course.enrolledUsers.length" class="pa-4 text-center">
            <p class="text-sm text-gray-400">No users enrolled yet</p>
          </div>
        </v-card>

        <!-- Course Info Card -->
        <v-card class="rounded-lg mt-4" outlined>
          <v-card-title class="text-sm font-bold pb-2" style="color: #21427d">
            <v-icon small class="mr-2" color="#21427d">mdi-information-outline</v-icon>
            Course Info
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-3">
            <div class="info-row mb-2">
              <span class="text-xs text-gray-500">Category</span>
              <v-chip x-small color="#e8eef8" text-color="#21427d">{{ course.category }}</v-chip>
            </div>
            <div class="info-row mb-2">
              <span class="text-xs text-gray-500">Difficulty</span>
              <v-chip x-small :color="difficultyColor" dark>{{ course.difficulty }}</v-chip>
            </div>
            <div class="info-row mb-2">
              <span class="text-xs text-gray-500">Instructor</span>
              <span class="text-xs font-semibold">{{ course.instructor }}</span>
            </div>
            <div class="info-row">
              <span class="text-xs text-gray-500">Created</span>
              <span class="text-xs font-semibold">{{ formatDate(course.createdAt) }}</span>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- Lesson Form Dialog -->
    <LessonFormDialog
      v-model="showLessonForm"
      :editData="editLessonData"
      :courseId="course.rawCourse ? course.rawCourse._id : course._id"
      :moduleId="
        course.rawCourse && course.rawCourse.modules && course.rawCourse.modules[0]
          ? course.rawCourse.modules[0]._id
          : null
      "
      :lessonId="editLessonData ? editLessonData._id : null"
      @save="handleLessonSave"
    />

    <!-- Standalone Video Upload for existing lessons -->
    <VideoUploadDialog
      v-if="showVideoUploadForLesson && videoUploadLesson"
      v-model="showVideoUploadForLesson"
      :courseId="course.rawCourse ? course.rawCourse._id : course._id"
      :moduleId="videoUploadLesson.moduleId"
      :lessonId="videoUploadLesson._id"
      @uploaded="onVideoUploaded"
    />

    <!-- Course Edit Dialog -->
    <CourseFormDialog v-model="showCourseEditForm" :editData="course" @save="handleCourseEdit" />

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="400">
      <v-card class="rounded-lg">
        <v-card-title class="text-base font-bold">
          <v-icon left color="red">mdi-alert-circle</v-icon>
          {{ deleteType === 'course' ? 'Delete Course' : 'Delete Lesson' }}
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ deleteTargetName }}</strong
          >? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="normal-case" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="red" dark class="normal-case" depressed @click="executeDelete"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  state,
  isLessonCompleted,
  markLessonComplete,
  markLessonIncomplete,
  getCourseProgress,
  getUserById,
  updateCourse,
  deleteCourse,
  addLesson,
  updateLesson,
  deleteLesson,
  initLearningData
} from './learningData'
import LessonFormDialog from './LessonFormDialog.vue'
import CourseFormDialog from './CourseFormDialog.vue'
import VideoUploadDialog from './VideoUploadDialog.vue'

export default {
  name: 'CourseDetail',
  components: { LessonFormDialog, CourseFormDialog, VideoUploadDialog },
  props: {
    course: { type: Object, required: true },
    currentUserId: { type: String, default: 'u1' }
  },
  data() {
    return {
      openPanels: [],
      showLessonForm: false,
      showCourseEditForm: false,
      editLessonData: null,
      showDeleteConfirm: false,
      deleteType: 'course',
      deleteTargetId: null,
      deleteTargetName: '',
      showVideoUploadForLesson: false,
      videoUploadLesson: null
    }
  },
  computed: {
    sortedLessons() {
      return [...this.course.lessons].sort((a, b) => a.order - b.order)
    },
    totalDuration() {
      let totalMinutes = 0
      this.course.lessons.forEach((l) => {
        const parts = l.duration.split(':')
        totalMinutes += parseInt(parts[0] || 0)
        totalMinutes += parseInt(parts[1] || 0) / 60
      })
      const hrs = Math.floor(totalMinutes / 60)
      const mins = Math.round(totalMinutes % 60)
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`
    },
    myProgress() {
      void state.progress.length
      return getCourseProgress(this.course._id, this.currentUserId)
    },
    difficultyColor() {
      const map = { Beginner: '#4caf50', Intermediate: '#ff9800', Advanced: '#f44336' }
      return map[this.course.difficulty] || '#9e9e9e'
    }
  },
  methods: {
    isLessonDone(lessonId) {
      void state.progress.length
      return isLessonCompleted(this.course._id, lessonId, this.currentUserId)
    },
    async toggleLessonCompletion(lessonId, checked) {
      if (checked) {
        await markLessonComplete(this.$axios, this.course._id, lessonId, this.currentUserId)
      } else {
        await markLessonIncomplete(this.$axios, this.course._id, lessonId, this.currentUserId)
      }
      await initLearningData(this.$axios)
    },
    playLesson(lesson) {
      this.$emit('play-lesson', lesson)
    },
    editCourse() {
      this.showCourseEditForm = true
    },
    async handleCourseEdit(formData) {
      await updateCourse(this.$axios, this.course._id, formData)
      await initLearningData(this.$axios)
    },
    confirmDelete() {
      this.deleteType = 'course'
      this.deleteTargetName = this.course.title
      this.showDeleteConfirm = true
    },
    editLesson(lesson) {
      this.editLessonData = lesson
      this.showLessonForm = true
    },
    deleteLessonConfirm(lesson) {
      this.deleteType = 'lesson'
      this.deleteTargetId = lesson._id
      this.deleteTargetName = lesson.title
      this.showDeleteConfirm = true
    },
    async executeDelete() {
      if (this.deleteType === 'course') {
        await deleteCourse(this.$axios, this.course._id)
        await initLearningData(this.$axios)
        this.$emit('back')
      } else {
        await deleteLesson(this.$axios, this.course._id, this.deleteTargetId)
        await initLearningData(this.$axios)
      }
      this.showDeleteConfirm = false
    },
    async handleLessonSave(formData) {
      if (this.editLessonData) {
        await updateLesson(this.$axios, this.course._id, this.editLessonData._id, formData)
        this.editLessonData = null
      } else {
        await addLesson(this.$axios, this.course._id, formData)
      }
      await initLearningData(this.$axios)
    },
    openVideoUpload(lesson) {
      this.videoUploadLesson = lesson
      this.showVideoUploadForLesson = true
    },
    onVideoUploaded() {
      // Refresh course data so new video content is visible
      initLearningData(this.$axios)
    },
    getUserName(uid) {
      const user = getUserById(uid)
      return user ? user.name : uid
    },
    getUserInitials(uid) {
      const user = getUserById(uid)
      if (!user) return '?'
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    },
    getUserProgress(uid) {
      void state.progress.length
      return getCourseProgress(this.course._id, uid)
    },
    getProgressColor(progress) {
      if (progress >= 100) return '#4caf50'
      if (progress >= 60) return '#2196f3'
      if (progress >= 30) return '#ff9800'
      return '#f44336'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.course-hero {
  background: linear-gradient(135deg, #21427d 0%, #3a6bc5 60%, #5e8fd6 100%);
  position: relative;
  overflow: hidden;
}
.course-hero::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}
.course-hero::after {
  content: '';
  position: absolute;
  bottom: -40px;
  left: 30%;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}
.hero-stat {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  font-weight: 600;
}

.lesson-accordion .lesson-panel {
  border: 1px solid #e8eef8;
  transition: all 0.2s ease;
}
.lesson-accordion .lesson-panel:hover {
  border-color: #21427d;
  box-shadow: 0 2px 8px rgba(33, 66, 125, 0.08);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.course-sidebar {
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .course-sidebar {
    width: 100% !important;
  }
}
</style>
